/**
 * The link/session resume flow — split out of `OnlineMenuHost.ts` to keep
 * that file under the 200-line cap. Takes only the collaborators it needs
 * (never the host class itself) so `OnlineMenuHost.resumeRoom` stays a thin
 * call-through and this module stays independently testable.
 */

import {
  resumeRoom as resumeRoomFlow,
  type RoomClient,
  type SessionStore,
  type ResumeFailureCause,
} from '@bn-games/room-client';
import { isLoggedIn, fetchMe } from '@bn-games/auth-client';
import { openModal, openResumeFailureModal } from '@bn-games/ui-shell';
import { DEFAULT_PLAYER_NAME } from '../network/roomOptions.js';
import { HOST_SEAT } from './onlineHostConfig.js';
import { t } from '../i18n/I18n.js';
import type { ClientMessage, ServerMessage } from '../network/protocol.js';
import type { Scene } from '../ui/types.js';

export interface OnlineResumeDeps {
  readonly room: RoomClient<ClientMessage, ServerMessage>;
  readonly sessionStore: SessionStore;
  readonly navigate: (scene: Scene) => void;
  /** Host is only known once seated — see `performRoomResume`. */
  readonly setHost: (host: boolean) => void;
  /**
   * Tracks the `abandon` callback `resumeRoomFlow` hands back while a resume
   * is seated-but-waiting (room not yet full), so `OnlineMenuHost.disconnect`
   * can call it before `room.cancel()` — otherwise `resumeRoomFlow`'s
   * `onUpdate`/`onReconnectFail` listeners stay subscribed to this same
   * long-lived `RoomClient` for the rest of its lifetime. See
   * `resumeRoom`'s `onSeatedWaiting` doc comment (`@bn-games/room-client`).
   */
  readonly setPendingAbandon: (abandon: (() => void) | null) => void;
}

/**
 * Resumes a held seat by room code — the entry point for both an accepted
 * invite link's `#room=CODE` hash (T7) and the stored-session "Resume
 * online" menu button (T8). A room that hasn't filled yet lands on the
 * lobby's waiting roster instead of sitting behind the modal indefinitely.
 */
export function performRoomResume(
  deps: OnlineResumeDeps,
  roomCode: string,
  legacyToken?: string,
): Promise<void> {
  if (legacyToken) deps.sessionStore.saveFor(roomCode, legacyToken);
  deps.setHost(false);
  const modal = openModal({
    title: t('online.resumeModal.reconnectingTitle'),
    body: t('online.resumeModal.reconnectingBody'),
    actions: [],
    dismissible: false,
  });
  return resumeRoomFlow(deps.room, {
    roomCode,
    playerName: DEFAULT_PLAYER_NAME,
    onSeatedWaiting: (abandon) => {
      // A host who reloads before the table fills reclaims slot 0 back into
      // the lobby, and still owns "Start now" there.
      deps.setHost(deps.room.getInfo().localSeat === HOST_SEAT);
      deps.setPendingAbandon(abandon);
      modal.close();
      deps.navigate('online');
    },
  }).then((outcome) => {
    deps.setPendingAbandon(null);
    modal.close();
    if (outcome.kind === 'failed') {
      showResumeFailure(deps, outcome.cause, roomCode, legacyToken);
    }
  });
}

function showResumeFailure(
  deps: OnlineResumeDeps,
  cause: ResumeFailureCause,
  roomCode: string,
  legacyToken?: string,
): void {
  openResumeFailureModal({
    cause,
    signedIn: isLoggedIn(),
    onRetry: () => {
      void performRoomResume(deps, roomCode, legacyToken);
    },
    onSignIn: () => {
      void fetchMe();
    },
  });
}
